/** Главный экран: карта на весь экран и шторка с маршрутом поверх неё. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ConfigResponse, LatLon, MeResponse } from '@cdf/shared';

import { api } from '../api/client';
import { BottomSheet } from '../components/BottomSheet';
import { MapPlaceholder } from '../components/MapPlaceholder';
import { MapView, type MapHandle } from '../components/MapView';
import { PlanSummary } from '../components/PlanSummary';
import { SettingsSheet } from '../components/SettingsSheet';
import { SummarySkeleton, StopListSkeleton } from '../components/Skeleton';
import { EmptyState, ErrorState, OfflineBanner } from '../components/StateBlocks';
import { StopList } from '../components/StopList';
import { useBottomSheet } from '../hooks/useBottomSheet';
import { useGeolocation } from '../hooks/useGeolocation';
import { useMonitor } from '../hooks/useMonitor';
import { useOnline } from '../hooks/useOnline';
import { usePlan } from '../hooks/usePlan';
import { useSettings } from '../hooks/useSettings';
import { useToasts } from '../hooks/useToasts';
import { formatClock, formatNumber, plural } from '../lib/format';

interface MainScreenProps {
  config: ConfigResponse;
  me: MeResponse;
  onLoggedOut(): void;
}

export function MainScreen({ config, me, onLoggedOut }: MainScreenProps): JSX.Element {
  const toasts = useToasts();
  const online = useOnline();
  const geo = useGeolocation();
  const plan = usePlan();
  const settings = useSettings(config.defaultSettings);
  const sheet = useBottomSheet('mid');

  const mapRef = useRef<MapHandle>(null);
  const startRef = useRef<LatLon | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busyIndex, setBusyIndex] = useState<number | null>(null);
  const [mapBroken, setMapBroken] = useState(false);

  const monitor = useMonitor({
    enabled: true,
    planId: plan.plan?.id ?? null,
    onPlanUpdated: plan.replacePlan,
  });

  // --- Позиция и построение маршрута ---
  useEffect(() => {
    // Точка, заданная руками, уже восстановлена из хранилища — спрашивать
    // геопозицию поверх неё незачем, иначе отказ в доступе её затрёт.
    if (!geo.point) geo.locate();
    // Запрашиваем позицию ровно один раз при входе на экран.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlan = plan.load;
  const rebuild = useCallback(
    (start: LatLon) => {
      startRef.current = start;
      void loadPlan(start, settings.settings);
    },
    [loadPlan, settings.settings],
  );

  // Сначала пробуем поднять план, который сервер помнит по этой сессии:
  // перезагрузка страницы посреди смены не должна ронять маршрут.
  const restorePlan = plan.restore;
  const bootedRef = useRef(false);
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    void restorePlan(settings.settings.searchRadiusKm);
  }, [restorePlan, settings.settings.searchRadiusKm]);

  // Строим новый план, только если восстанавливать было нечего.
  useEffect(() => {
    if (plan.restoring || plan.plan || startRef.current || !geo.point) return;
    rebuild(geo.point);
  }, [geo.point, rebuild, plan.restoring, plan.plan]);

  const handlePick = useCallback(
    (point: LatLon) => {
      geo.setManual(point);
      rebuild(point);
      mapRef.current?.centerOn(point, 14);
    },
    [geo, rebuild],
  );

  const refresh = useCallback(() => {
    const start = geo.point ?? startRef.current;
    if (!start) {
      geo.locate();
      return;
    }
    rebuild(start);
  }, [geo, rebuild]);

  // --- Действия по шагам ---
  const runStopAction = useCallback(
    async (index: number, action: 'done' | 'skip') => {
      setBusyIndex(index);
      try {
        if (action === 'done') {
          await plan.markDone(index);
          toasts.push({
            kind: 'success',
            title: `Шаг ${index + 1} закрыт`,
            text: `+${formatNumber(settings.settings.bonusPerRefuel)} ₽`,
          });
        } else {
          await plan.markSkipped(index);
        }
        monitor.dismissAlert(index);
      } catch {
        toasts.push({
          kind: 'danger',
          title: 'Сервер не принял отметку',
          text: 'Отметка сохранена локально, попробуйте обновить маршрут.',
        });
      } finally {
        setBusyIndex(null);
      }
    },
    [plan, toasts, monitor, settings.settings.bonusPerRefuel],
  );

  // --- Производные данные ---
  const alertIndices = useMemo(
    () => new Set(monitor.alerts.keys()),
    [monitor.alerts],
  );

  const earnedBonus = useMemo(() => {
    if (!plan.plan) return 0;
    return plan.plan.stops
      .filter((stop) => plan.done.has(stop.index))
      .reduce((sum, stop) => sum + stop.bonus, 0);
  }, [plan.plan, plan.done]);

  const focusStop = useCallback(
    (index: number) => {
      const stop = plan.plan?.stops.find((item) => item.index === index);
      if (stop) mapRef.current?.centerOn(stop.car.location, 16);
      sheet.snapTo('mid');
      // Тап по булавке должен подводить и к карточке — иначе связь карты
      // со списком приходится искать глазами.
      window.requestAnimationFrame(() => {
        document
          .querySelector(`[data-stop="${index}"]`)
          ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    },
    [plan.plan, sheet],
  );

  // Карта теперь есть всегда, и тап по ней — основной способ задать старт без
  // геолокации. Поэтому режим выбора включён во всех случаях, кроме поиска
  // позиции: иначе отказ в доступе оставлял бы экран вообще без точки старта.
  const pickMode = !geo.point && geo.status !== 'locating';

  /** Выход: сессию рвём на сервере, но интерфейс не держим, если запрос упал. */
  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      onLoggedOut();
    }
  }, [onLoggedOut]);

  // --- Карта ---
  // Тайлы OSM бесплатны и без ключей, поэтому карта показывается всегда;
  // заглушка остаётся только на случай, когда движок или тайлы не поднялись.
  const mapNode = !mapBroken ? (
    <MapView
      ref={mapRef}
      user={geo.point}
      minLitersForBonus={settings.settings.minLitersForBonus}
      cars={plan.cars}
      plan={plan.plan}
      stations={plan.stations}
      activeIndex={plan.currentIndex}
      alertIndices={alertIndices}
      doneIndices={plan.done}
      bottomPadding={sheet.visibleHeight}
      pickMode={pickMode}
      onPick={handlePick}
      onSelectStop={focusStop}
      onFailure={(reason) => {
        setMapBroken(true);
        toasts.push({
          kind: 'info',
          title: 'Карта недоступна',
          text: `Работаем списком: ${reason}`,
        });
      }}
    />
  ) : (
    <MapPlaceholder
      title="Карта не загрузилась"
      text="Тайлы OpenStreetMap не ответили. Маршрут и список шагов работают как обычно."
      point={geo.point}
      onPick={handlePick}
      onOpenForm={() => sheet.snapTo('collapsed')}
    />
  );

  // --- Содержимое шторки ---
  const busyWithoutPlan = (plan.status === 'loading' || plan.restoring) && !plan.plan;

  const peek = busyWithoutPlan ? (
    <SummarySkeleton />
  ) : plan.plan ? (
    <PlanSummary
      plan={plan.plan}
      doneCount={plan.done.size}
      skippedCount={plan.skipped.size}
      earnedBonus={earnedBonus}
    />
  ) : (
    <div className="summary summary--empty">
      <span className="summary__label">Маршрут</span>
      <strong className="summary__value">
        {plan.status === 'error' ? 'Не построен' : 'Готовим…'}
      </strong>
    </div>
  );

  const body = (() => {
    if (plan.status === 'error' && plan.error) {
      return (
        <ErrorState
          title={plan.error.isNetwork ? 'Нет связи' : 'Не получилось построить маршрут'}
          text={plan.error.message}
          onRetry={refresh}
          retrying={false}
        />
      );
    }

    if (busyWithoutPlan) {
      return <StopListSkeleton />;
    }

    if (!geo.point) {
      return (
        <EmptyState
          icon="📍"
          title={geo.status === 'locating' ? 'Ищем вас на карте' : 'Нужна точка старта'}
          text={
            geo.message ??
            'Разрешите доступ к геопозиции или тапните по карте — от точки старта считается вся цепочка.'
          }
          action={
            <>
              <button type="button" className="btn btn--primary" onClick={geo.locate}>
                Определить позицию
              </button>
              {/* Шторка закрывает карту, а тапать надо именно по ней. */}
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => sheet.snapTo('collapsed')}
              >
                Указать на карте
              </button>
            </>
          }
        />
      );
    }

    if (!plan.plan || plan.plan.stops.length === 0) {
      return (
        <EmptyState
          icon="🔎"
          title="Рядом нечего заправлять"
          text={`В радиусе ${settings.settings.searchRadiusKm} км не нашлось машин, куда влезет ${settings.settings.minLitersForBonus} л и больше. Увеличьте радиус или подождите.`}
          action={
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setSettingsOpen(true)}
            >
              Настройки расчёта
            </button>
          }
        />
      );
    }

    return (
      <>
        {plan.plan.warnings.length > 0 ? (
          <ul className="warnings">
            {plan.plan.warnings.map((warning) => (
              <li className="warnings__item" key={warning}>
                {warning}
              </li>
            ))}
          </ul>
        ) : null}

        {plan.plan.warnings.length === 0 && !plan.plan.routingPrecise ? (
          <p className="note">
            Времена оценены эвристикой — точный роутинг 2GIS недоступен.
          </p>
        ) : null}

        <StopList
          plan={plan.plan}
          done={plan.done}
          minLitersForBonus={settings.settings.minLitersForBonus}
          skipped={plan.skipped}
          currentIndex={plan.currentIndex}
          alerts={monitor.alerts}
          flashIndex={monitor.flashIndex}
          busyIndex={busyIndex}
          onDone={(index) => void runStopAction(index, 'done')}
          onSkip={(index) => void runStopAction(index, 'skip')}
          onShowOnMap={focusStop}
        />

        <div className="sheet__footer">
          <p className="sheet__meta">
            {plan.rejectedCount > 0
              ? `Отброшено ${plan.rejectedCount} ${plural(plan.rejectedCount, 'машина', 'машины', 'машин')} · `
              : ''}
            {plan.fetchedAtISO ? `данные на ${formatClock(plan.fetchedAtISO)}` : ''}
          </p>
          <button type="button" className="btn btn--ghost btn--block" onClick={refresh}>
            Обновить маршрут
          </button>
        </div>
      </>
    );
  })();

  // Число берём из своего запроса машин — оно привязано к радиусу пользователя.
  // Тик монитора уточняет его, когда приходит с непустой выдачей.
  const nearby =
    monitor.lastTick && monitor.lastTick.eligible > 0
      ? monitor.lastTick.eligible
      : plan.cars.length;

  const monitorLabel =
    nearby > 0
      ? `${nearby} ${plural(nearby, 'машина', 'машины', 'машин')} рядом`
      : monitor.connection === 'open'
        ? 'Монитор на связи'
        : monitor.connection === 'connecting'
          ? 'Подключаемся…'
          : 'Монитор офлайн';

  return (
    <div className="screen">
      {mapNode}

      <div className="topbar">
        <div className="topbar__left">
          <span
            className={`status status--${monitor.connection}`}
            aria-live="polite"
          >
            <span className="status__dot" aria-hidden="true" />
            {monitorLabel}
          </span>
          {config.mock || plan.mock ? (
            <span className="badge badge--mock">Демо-данные</span>
          ) : null}
        </div>

        <div className="topbar__right">
          <button
            type="button"
            className="iconbtn iconbtn--float"
            onClick={() => {
              if (geo.point) mapRef.current?.centerOn(geo.point, 15);
              else geo.locate();
            }}
            aria-label="Показать мою позицию"
          >
            ◎
          </button>
          <button
            type="button"
            className="iconbtn iconbtn--float"
            onClick={() => setSettingsOpen(true)}
            aria-label="Настройки расчёта"
          >
            ⚙
          </button>
          <button
            type="button"
            className="iconbtn iconbtn--float"
            onClick={() => void logout()}
            aria-label={`Выйти из аккаунта ${me.phone ?? ''}`}
          >
            ⏻
          </button>
        </div>
      </div>

      {!online ? <OfflineBanner /> : null}

      <BottomSheet sheet={sheet} label="Маршрут заправок" peek={peek}>
        {body}
      </BottomSheet>

      <SettingsSheet
        open={settingsOpen}
        settings={settings.settings}
        defaults={config.defaultSettings}
        dirty={settings.dirty}
        onPatch={settings.patch}
        onReset={settings.reset}
        onClose={() => setSettingsOpen(false)}
        onApply={refresh}
      />
    </div>
  );
}

